import type { ColumnDef, SortingState } from "@tanstack/react-table";

import { useMemo } from "react";

import type { ChildDisplayStatus, SubIB, SubIBHoid } from "../types";
import type { KebabAction } from "./KebabMenu";

import { Button as ButtonV2 } from "../../Button";
import {
  AddToAListIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  ErrorIconV2,
  InfoIcon,
  Users2Icon,
} from "../../Icons";
import { TableDesktop } from "../../Table";
import { TooltipWrapper } from "../../Tooltip";
import { Typo } from "../../Typo";
import { EMPTY_DATE } from "../consts";
import { AnalyticsIconButton } from "./AnalyticsIconButton";
import { CommentCell } from "./CommentCell";
import { KebabMenu } from "./KebabMenu";
import { NewTag } from "./NewTag";
import { StatusPill } from "./StatusPill";

const PAGE_SIZE = 10;
const NEW_TAG_DAYS = 30;

const isRecentlyAdded = (dateStr: string | null): boolean => {
  if (!dateStr) return false;
  const t = new Date(dateStr).getTime();
  if (Number.isNaN(t)) return false;
  const days = (Date.now() - t) / 86400000;
  return days >= -1 && days <= NEW_TAG_DAYS;
};

const fmtDate = (d: string | null): string => {
  if (!d) return EMPTY_DATE;
  return new Date(d).toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const fmtUsd = (n: number): string =>
  "US$" +
  n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const HeaderCell = ({ text, info }: { text: string; info?: string }) => (
  <span className="inline-flex items-center gap-1">
    <Typo element="span" type="bodyXS" textColor="text-text-secondary">
      {text}
    </Typo>
    {info && (
      <TooltipWrapper
        text={info}
        color="blue"
        cursor="help"
        // See the matching HeaderCell in rebate-programs/SharedRebateProgramsView
        // — header tooltips open upward, and only stay there once the panel
        // stops being bounded by the table's scroll wrapper.
        tooltipPlacement="top"
        positionStrategy="fixed"
        tooltipMaxWidth={240}
        tooltipContainerClassName="!text-xs"
      >
        <InfoIcon
          className="h-3.5 w-3.5 shrink-0 text-text-secondary"
          aria-hidden="true"
          focusable="false"
        />
      </TooltipWrapper>
    )}
  </span>
);

export type KebabActionContext =
  | { kind: "parent"; sub: SubIB }
  | { kind: "child"; hoid: SubIBHoid };

type FlatRow =
  | { kind: "parent"; sub: SubIB }
  | { kind: "child"; hoid: SubIBHoid; parentKycPending: boolean };

const childDisplayStatus = (
  hoid: SubIBHoid,
  parentKycPending: boolean,
): ChildDisplayStatus => {
  if (hoid.status === "archived") return "archived";
  if (parentKycPending) return "pendingKyc";
  return "active";
};

const parentDisplayStatus = (sub: SubIB): ChildDisplayStatus => {
  if (sub.kycStatus === "pending") return "pendingKyc";
  return sub.aggregateStatus === "active" ? "active" : "inactive";
};

const compareValue = (key: string, sub: SubIB): number | string => {
  switch (key) {
    case "name":
      return sub.name.toLowerCase();
    case "rebateProgram":
      return sub.hoids.length;
    case "rebateSplit":
      return sub.rebateSplitMin;
    case "lastPayment":
      return sub.lastPayment ? new Date(sub.lastPayment).getTime() : 0;
    case "subIbPayout":
      return sub.aggregateSubIbPayout;
    case "subIbSince":
      return new Date(sub.addedAt).getTime();
    default:
      return 0;
  }
};

// TanStack's `getCanSort()` requires a truthy `accessorFn`/`accessorKey` on
// the column def — without one it silently reports false regardless of
// `enableSorting`, which is why the shared header sort icon/click never
// appeared. Actual row order is fully manual (see `sortSubs`/`compareValue`
// above) — this only needs to exist, not be authoritative.
const accessorForKey =
  (key: string) =>
  (row: FlatRow): number | string =>
    row.kind === "parent" ? compareValue(key, row.sub) : "";

const sortSubs = (subs: SubIB[], sorting: SortingState): SubIB[] => {
  const sort = sorting[0];
  if (!sort) {
    // Default: Sub IB since, latest → oldest.
    return [...subs].sort(
      (a, b) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime(),
    );
  }
  const sign = sort.desc ? -1 : 1;
  return [...subs].sort((a, b) => {
    const av = compareValue(sort.id, a);
    const bv = compareValue(sort.id, b);
    if (av < bv) return -1 * sign;
    if (av > bv) return 1 * sign;
    return 0;
  });
};

/**
 * Empty and error states for the Sub IBs table.
 *
 * Mirrors the design's `TableState`, which is deliberately **one** component
 * covering both: same layout and typography, only the illustration, copy and the
 * retry action differ. Two things follow from that and are easy to get wrong:
 *
 * - There is **no separate "no results" state**. The design renders `empty` for
 *   both "nothing yet" and "nothing matched", which is why its copy names both
 *   escapes ("change your filters **or** add a new sub IB"). Don't reintroduce a
 *   search-specific variant.
 * - **Empty has no call to action.** Only the error state gets a button; the
 *   design gates that on `state === "error"`.
 *
 * Not built on the shared `ApiError`: that renders labelLG/bodyMD with its own
 * gaps and button, which is off this spec, and it has three other consumers who
 * would move with it.
 */
const SubIBsTableState = ({
  state,
  onRetry,
}: {
  state: "empty" | "error";
  onRetry: () => void;
}) => {
  const isError = state === "error";
  return (
    <div
      className="mx-auto flex h-96 flex-col items-center justify-center gap-4"
      role={isError ? "alert" : "status"}
      data-testid={isError ? "table-error" : "no-records-found"}
    >
      {isError ? (
        <ErrorIconV2 width={64} height={64} aria-hidden="true" />
      ) : (
        <AddToAListIcon
          className="h-16 w-16"
          aria-hidden="true"
          focusable="false"
        />
      )}
      <div className="flex flex-col items-center gap-2 text-center">
        <Typo element="span" type="labelMD" textColor="text-text-primary">
          {isError ? "Unable to load data" : "No data available"}
        </Typo>
        <Typo element="span" type="bodySM" textColor="text-text-secondary">
          {isError
            ? "Refresh to try again."
            : "Try changing your filters or add a new sub IB"}
        </Typo>
      </div>
      {isError && (
        <ButtonV2 variant="brand" size="md" onClick={onRetry}>
          Refresh
        </ButtonV2>
      )}
    </div>
  );
};

type SubIBsTableProps = {
  rows: SubIB[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  sorting: SortingState;
  onSortingChange: (
    updater: SortingState | ((old: SortingState) => SortingState),
  ) => void;
  page: number;
  onPageChange: (page: number) => void;
  expanded: Set<string>;
  onToggleExpand: (id: string) => void;
  highlightedRowId?: string;
  onRowClick: (row: SubIB) => void;
  onEditComment: (row: SubIB) => void;
  onKebabAction: (action: KebabAction, ctx: KebabActionContext) => void;
};

export const SubIBsTable = ({
  rows,
  isLoading,
  isError,
  onRetry,
  sorting,
  onSortingChange,
  page,
  onPageChange,
  expanded,
  onToggleExpand,
  highlightedRowId,
  onRowClick,
  onEditComment,
  onKebabAction,
}: SubIBsTableProps) => {
  const sortedSubs = useMemo(() => sortSubs(rows, sorting), [rows, sorting]);
  const pageCount = Math.max(1, Math.ceil(sortedSubs.length / PAGE_SIZE));
  const pageSubs = useMemo(
    () => sortedSubs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [sortedSubs, page],
  );

  const flatRows: FlatRow[] = useMemo(() => {
    const out: FlatRow[] = [];
    pageSubs.forEach((sub) => {
      out.push({ kind: "parent", sub });
      if (expanded.has(sub.id) && sub.hoids.length > 1) {
        const children = [...sub.hoids].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        children.forEach((hoid) => {
          out.push({
            kind: "child",
            hoid,
            parentKycPending: sub.kycStatus === "pending",
          });
        });
      }
    });
    return out;
  }, [pageSubs, expanded]);

  const columns: ColumnDef<FlatRow, any>[] = [
    {
      id: "name",
      header: () => <HeaderCell text="Sub IB" />,
      accessorFn: accessorForKey("name"),
      enableSorting: true,
      meta: {
        headerClassName: "w-[24%] whitespace-nowrap",
        cellClassName: "w-[24%]",
      },
      cell: ({ row }) => {
        const r = row.original;
        if (r.kind === "child") {
          return (
            <div className="-m-3 flex items-center justify-between gap-2 border-l-[3px] border-surface-brand-default p-3 pl-[37px]">
              <div className="flex min-w-0 flex-col gap-0.5">
                <div className="flex flex-wrap items-center gap-2 [row-gap:4px]">
                  <Typo
                    element="span"
                    type="labelSM"
                    textColor="text-text-primary"
                    className="truncate"
                  >
                    {r.hoid.rebateProgramLabel}
                  </Typo>
                  {isRecentlyAdded(r.hoid.createdAt) && <NewTag />}
                  <StatusPill
                    status={childDisplayStatus(r.hoid, r.parentKycPending)}
                  />
                </div>
                <Typo
                  element="span"
                  type="bodyXS"
                  textColor="text-text-secondary"
                >
                  Rebate program
                </Typo>
              </div>
              <div className="flex items-center gap-1">
                <AnalyticsIconButton
                  subject={r.hoid.rebateProgramLabel}
                  onClick={() =>
                    onKebabAction("view-analytics", {
                      kind: "child",
                      hoid: r.hoid,
                    })
                  }
                  className="flex h-8 w-8 items-center justify-center rounded-full text-icon-brand-default hover:bg-overlay-opacity-state-selected"
                />
                <KebabMenu
                  kind="child"
                  status={childDisplayStatus(r.hoid, r.parentKycPending)}
                  onAction={(action) =>
                    onKebabAction(action, { kind: "child", hoid: r.hoid })
                  }
                />
              </div>
            </div>
          );
        }
        const sub = r.sub;
        return (
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => onRowClick(sub)}
              className="flex min-w-0 flex-1 flex-col gap-0.5 text-left"
            >
              <div className="flex w-full flex-wrap items-center gap-2 [row-gap:4px]">
                <Typo
                  element="span"
                  type="labelSM"
                  textColor="text-text-primary"
                  className="min-w-0 max-w-full truncate"
                >
                  {sub.name}
                </Typo>
                {isRecentlyAdded(sub.addedAt) && <NewTag />}
                <StatusPill status={parentDisplayStatus(sub)} />
              </div>
              <Typo
                element="span"
                type="bodyXS"
                textColor="text-text-secondary"
                className="w-full truncate"
              >
                {sub.email}
              </Typo>
            </button>
            <div className="flex shrink-0 items-center gap-1">
              <AnalyticsIconButton
                subject={sub.name}
                onClick={() =>
                  onKebabAction("view-analytics", { kind: "parent", sub })
                }
                className="flex h-8 w-8 items-center justify-center rounded-full text-icon-brand-default hover:bg-overlay-opacity-state-selected"
              />
              <KebabMenu
                kind="parent"
                status={parentDisplayStatus(sub)}
                isMultiHoid={sub.hoids.length > 1}
                hasActiveProgram={sub.hoids.some((h) => h.status === "active")}
                onAction={(action) =>
                  onKebabAction(action, { kind: "parent", sub })
                }
              />
            </div>
          </div>
        );
      },
    },
    {
      id: "rebateProgram",
      header: () => <HeaderCell text="Rebate programs" />,
      accessorFn: accessorForKey("rebateProgram"),
      enableSorting: true,
      meta: {
        headerClassName: "w-[15%] whitespace-nowrap",
        cellClassName: "w-[15%]",
      },
      cell: ({ row }) => {
        const r = row.original;
        if (r.kind === "child") {
          return (
            <Typo element="span" type="bodyMD" textColor="text-text-primary">
              {r.hoid.rebateProgramLabel}
            </Typo>
          );
        }
        const sub = r.sub;
        if (sub.hoids.length === 0) {
          return (
            <Typo element="span" type="bodySM" textColor="text-text-secondary">
              —
            </Typo>
          );
        }
        if (sub.hoids.length > 1) {
          const isExpanded = expanded.has(sub.id);
          return (
            <button
              type="button"
              onClick={() => onToggleExpand(sub.id)}
              aria-expanded={isExpanded}
              className="flex items-center gap-1.5"
            >
              <Typo element="span" type="bodyMD" textColor="text-text-primary">
                {sub.hoids.length} programs
              </Typo>
              {isExpanded ? (
                <ArrowUpIcon className="h-3.5 w-3.5" />
              ) : (
                <ArrowDownIcon className="h-3.5 w-3.5" />
              )}
            </button>
          );
        }
        const hoid = sub.hoids[0];
        const shared = hoid.partners.length > 1;
        return (
          <span className="flex items-center gap-1.5">
            <Typo element="span" type="bodyMD" textColor="text-text-primary">
              {hoid.rebateProgramLabel}
            </Typo>
            {shared && (
              <TooltipWrapper
                text={`Program shared with ${hoid.partners.find((p) => p.subIbId !== sub.id)?.name ?? "another Sub IB"}`}
                color="blue"
                tooltipMaxWidth={240}
                tooltipContainerClassName="!text-xs"
              >
                <span
                  role="img"
                  aria-label="Program shared with another Sub IB"
                  tabIndex={0}
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded"
                >
                  <Users2Icon className="h-4 w-4 text-icon-secondary" />
                </span>
              </TooltipWrapper>
            )}
          </span>
        );
      },
    },
    {
      id: "rebateSplit",
      header: () => (
        // Design change #5 (PART-3930): no info tooltip on Sub IB split.
        <HeaderCell text="Sub IB split" />
      ),
      accessorFn: accessorForKey("rebateSplit"),
      enableSorting: true,
      meta: {
        headerClassName: "w-[11%] whitespace-nowrap",
        cellClassName: "w-[11%]",
      },
      cell: ({ row }) => {
        const r = row.original;
        const text =
          r.kind === "child"
            ? `${r.hoid.rebateSplitPct}%`
            : r.sub.hoids.length === 0
              ? "—"
              : r.sub.rebateSplitMin === r.sub.rebateSplitMax
                ? `${r.sub.rebateSplitMin}%`
                : `${r.sub.rebateSplitMin}%–${r.sub.rebateSplitMax}%`;
        return (
          <Typo element="span" type="bodySM" textColor="text-text-primary">
            {text}
          </Typo>
        );
      },
    },
    {
      id: "lastPayment",
      header: () => (
        <HeaderCell
          text="Last payment"
          info="Most recent payout date for this Sub IB."
        />
      ),
      accessorFn: accessorForKey("lastPayment"),
      enableSorting: true,
      meta: {
        headerClassName: "w-[12%] whitespace-nowrap",
        cellClassName: "w-[12%]",
      },
      cell: ({ row }) => {
        const r = row.original;
        const value =
          r.kind === "child" ? r.hoid.lastPayment : r.sub.lastPayment;
        return (
          <Typo
            element="span"
            type="bodySM"
            // An absent date dims to disabled, matching the canvas's
            // `.dash { color: var(--text-disabled) }` and the Shared rebate
            // programs table, which already did this.
            textColor={value ? "text-text-primary" : "text-text-disabled"}
            className="whitespace-nowrap"
          >
            {fmtDate(value)}
          </Typo>
        );
      },
    },
    {
      id: "subIbPayout",
      header: () => (
        // Design change #5 (PART-3930): no info tooltip on Sub IB payout.
        <HeaderCell text="Sub IB payout" />
      ),
      accessorFn: accessorForKey("subIbPayout"),
      enableSorting: true,
      meta: {
        headerClassName: "w-[12%] whitespace-nowrap",
        cellClassName: "w-[12%]",
      },
      cell: ({ row }) => {
        const r = row.original;
        const value =
          r.kind === "child" ? r.hoid.subIbPayout : r.sub.aggregateSubIbPayout;
        return (
          <Typo
            element="span"
            type="bodySM"
            textColor="text-text-primary"
            className="whitespace-nowrap"
          >
            {value > 0 ? fmtUsd(value) : "US$0.00"}
          </Typo>
        );
      },
    },
    {
      id: "subIbSince",
      header: () => (
        <HeaderCell
          text="Sub IB since"
          info="The date this Sub IB was added to your network."
        />
      ),
      accessorFn: accessorForKey("subIbSince"),
      enableSorting: true,
      meta: {
        headerClassName: "w-[13%] whitespace-nowrap",
        cellClassName: "w-[13%]",
      },
      cell: ({ row }) => {
        const r = row.original;
        if (r.kind === "child") return null;
        return (
          <Typo
            element="span"
            type="bodySM"
            textColor={
              r.sub.addedAt ? "text-text-primary" : "text-text-disabled"
            }
            className="whitespace-nowrap"
          >
            {fmtDate(r.sub.addedAt)}
          </Typo>
        );
      },
    },
    {
      id: "comment",
      header: () => <HeaderCell text="Comment" />,
      enableSorting: false,
      meta: {
        headerClassName: "w-[13%] whitespace-nowrap",
        cellClassName: "w-[13%]",
      },
      cell: ({ row }) => {
        const r = row.original;
        if (r.kind === "child") return null;
        return (
          <CommentCell
            comment={r.sub.comment}
            onEdit={() => onEditComment(r.sub)}
          />
        );
      },
    },
  ];

  return (
    <TableDesktop<FlatRow>
      data={flatRows}
      columns={columns}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      /*
       * A parent row is two lines — name (labelSM, 14×1.5 = 21px) + 2px gap +
       * email (bodyXS, 12×1.5 = 18px) = 41px, plus the body cell's `p-3`
       * (12px × 2) = 65px. Without this the skeleton rows are shorter than the
       * real ones and the table visibly jumps when data lands; the design sizes
       * its skeleton to the loaded row height for exactly this reason.
       */
      skeletonRowHeight={65}
      sorting={sorting}
      onSortingChange={onSortingChange}
      enableServerSidePagination
      enabledServerSideSorting
      state={{ pagination: { pageIndex: page, pageSize: PAGE_SIZE } }}
      onPaginationChange={(updater: any) => {
        const next =
          typeof updater === "function"
            ? updater({ pageIndex: page, pageSize: PAGE_SIZE })
            : updater;
        onPageChange(next.pageIndex);
      }}
      pageCount={pageCount}
      totalCount={sortedSubs.length}
      highlightedRowId={highlightedRowId}
      getRowId={(row) =>
        row.kind === "parent" ? `parent-${row.sub.id}` : `child-${row.hoid.id}`
      }
      getRowClassName={(row) => {
        if (row.kind === "child") return "group bg-surface-brand-subtle";
        if (expanded.has(row.sub.id) && row.sub.hoids.length > 1) {
          return "group bg-surface-brand-subtle";
        }
        return "group";
      }}
      onRowClick={(row) => {
        if (row.kind === "parent") onRowClick(row.sub);
      }}
      className="[&_table]:table-fixed"
      filterFns={{} as any}
      emptyComponent={<SubIBsTableState state="empty" onRetry={onRetry} />}
      errorComponent={<SubIBsTableState state="error" onRetry={onRetry} />}
      translatedContent={{
        previousPage: "Previous",
        nextPage: "Next",
        page: "Page",
        of: "of",
        showRows: "Show rows",
      }}
    />
  );
};
